import * as React from 'react';
import {
  button as buttonCls,
  labelSpan as labelCls,
  spinnerCls,
  spinnerOverlay as spinnerOverlayCls,
  SIZE_ICON,
} from './Button.css';

export type ButtonVariant =
  /** Filled charcoal CTA — login, send, primary modal action. */
  | 'primary'
  /** Soft-fill blue informational — "Send to Client", "On it", "Send Email". */
  | 'secondary'
  /** Soft-fill green trust-positive — "Trust" on attention/approval cards. */
  | 'softGreen'
  /** Soft-fill red destructive (quiet) — "Reject" / rollback. */
  | 'softRed'
  /** Modal-cancel ghost (light border). */
  | 'ghost'
  /** Heavier ghost — "Stay in HBG", "Remind me" inside attention cards. */
  | 'ghostStrong'
  /** Inline link-style button — "+ Add new client…", "Forgot password?". */
  | 'link'
  /** Legacy thin-outline. Prefer `ghostStrong`. */
  | 'outline'
  /** Legacy alias for `secondary`. Prefer `secondary`. */
  | 'accent'
  /** Solid-red destructive (irreversible). For everyday destructive actions, use `softRed`. */
  | 'destructive';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual treatment. Default `primary`. */
  variant?: ButtonVariant;
  /** Shared control height: sm 30 / md 36 / lg 44 px. Default `md`. */
  size?: ButtonSize;
  /** Icon slotted before the label. */
  leftIcon?: React.ReactNode;
  /** Icon slotted after the label. */
  rightIcon?: React.ReactNode;
  /** Stretch to fill the container width. */
  fullWidth?: boolean;
  /**
   * Async-action pending state: swaps the label for an inline spinner,
   * sets `aria-busy`, and disables interaction.
   *
   * Distinct from the form family's `skeleton` prop (Input / Textarea /
   * Select): `loading` means "an action this control triggered is
   * in flight" (a live, focusable, disabled control); `skeleton` means
   * "this control's data hasn't arrived yet" (an inert placeholder
   * shimmer). They are intentionally different concepts, not aliases.
   */
  loading?: boolean;
  /** React 19 ref-as-prop (replaces the old forwardRef pattern). */
  ref?: React.Ref<HTMLButtonElement>;
}

function InlineSpinner({ size }: { size: number }) {
  return (
    <svg
      className={spinnerCls}
      viewBox="0 0 16 16"
      fill="none"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  fullWidth = false,
  loading = false,
  disabled,
  type = 'button',
  children,
  ref,
  className,
  ...rest
}: ButtonProps) {
  const cls = [buttonCls({ variant, size, fullWidth, loading }), className]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cls}
      {...rest}
    >
      {loading && (
        <span className={spinnerOverlayCls} aria-hidden="true">
          <InlineSpinner size={SIZE_ICON[size]} />
        </span>
      )}
      <span className={labelCls({ hidden: loading })}>
        {leftIcon}
        {children}
        {rightIcon}
      </span>
    </button>
  );
}
Button.displayName = 'Button';
